import { h, render } from 'vue'
import ConfirmModal from '~/components/base/ConfirmModal.vue'
import InfoPanelModal from '~/components/base/InfoPanelModal.vue'
import { confirmAction } from '~/utils/actionConfirmDialog'
import { isCurrentBelowDesktop } from '~/composables/useMediaQuery'
import { lockBodyScroll, unlockBodyScroll } from '~/utils/modal'

/**
 * @description 通用确认弹窗调用
 * @reason 封装命令式调用，支持在 JS 逻辑中直接触发，自动处理挂载与卸载，并支持响应式布局（由 ConfirmModal 内部实现）
 * @param {Object} options
 * @param {string} options.title - 标题
 * @param {string} options.content - 内容
 * @param {string} options.type - 弹窗类型 ('info' | 'warning' | 'success' | 'error')
 * @param {boolean} options.showCancel - 是否显示取消按钮
 * @param {boolean} options.showOk - 是否显示确认按钮
 * @param {string} options.okText - 确认按钮文案
 * @param {string} options.cancelText - 取消按钮文案
 * @param {string} options.okType - 确认按钮类型
 * @param {string} options.cancelType - 取消按钮类型
 * @param {Function} options.beforeOk - 确认前回调（返回 false 阻止关闭）
 * @param {Object} options.okStyle - 确认按钮样式
 * @param {Object} options.cancelStyle - 取消按钮样式
 * @param {boolean} options.showIcon - 是否显示图标
 * @param {number|string} options.width - 弹窗宽度
 * @param {boolean} options.closable - 是否显示右上角关闭按钮
 * @param {boolean} options.maskClosable - 是否允许点击蒙层关闭
 * @param {boolean} options.bottomSheetOnMobile - 移动端是否作为底部弹窗展示
 * @param {Object} options.contentComponent - 自定义内容组件
 * @param {Object} options.contentProps - 自定义内容组件 Props
 * @param {Object} options.contentSlots - 自定义插槽
 * @param {Function} options.onOk - 点击确认按钮的回调
 * @param {Function} options.onCancel - 仅点击取消按钮的回调
 * @param {Function} options.onClose - 窗口关闭的统一回调（确定、取消、点击×、点击蒙层都会触发）
 * @edge-cases 仅在客户端运行；自动注入 Nuxt Context 以确保组件内使用 composables 正常
 */
export const confirm = (options = {}) => {
  if (!import.meta.client) return

  const hasCustomContent = Boolean(options.contentComponent || options.contentSlots)
  const isMobileConfirmAction =
    isCurrentBelowDesktop() &&
    !hasCustomContent &&
    !options.bottomSheetOnMobile &&
    !options.mobileFullscreen &&
    ((options.showCancel ?? true) || (options.showOk ?? true))

  if (isMobileConfirmAction) {
    let isClosed = false
    const emitCloseOnce = (result) => {
      if (isClosed) return
      isClosed = true
      options.onClose?.(result)
    }
    const controller = confirmAction({
      title: options.title || '提示',
      content: options.content,
      showCancel: options.showCancel ?? true,
      showOk: options.showOk ?? true,
      cancelText: options.cancelText,
      okText: options.okText,
      maskClosable: options.maskClosable ?? true,
      onCancel: () => {
        options.onCancel?.()
        emitCloseOnce({ ok: false, cancel: true })
      },
      onOk: async () => {
        if (options.beforeOk) {
          const result = await options.beforeOk()
          if (result === false) return false
        }
        await options.onOk?.()
        emitCloseOnce({ ok: true, cancel: false })
        return true
      },
      onClose: emitCloseOnce,
    })

    return {
      close: (result = { ok: false, cancel: false }) => {
        emitCloseOnce(result)
        controller?.close?.()
      },
    }
  }

  const container = document.createElement('div')
  document.body.appendChild(container)
  lockBodyScroll()
  let isClosed = false

  const handleClose = (opt) => {
    if (isClosed) return
    isClosed = true
    options.onClose?.(opt)
    unlockBodyScroll()
    render(null, container)
    if (container.parentNode) {
      container.parentNode.removeChild(container)
    }
  }

  const closeModal = (result) => {
    handleClose(result)
  }

  const slots = options.contentSlots ? { ...options.contentSlots } : {}

  if (!slots.default && options.contentComponent) {
    const mergedContentProps = {
      ...(options.contentProps || {}),
      onSubmitted: (...args) => {
        options.contentProps?.onSubmitted?.(...args)
        closeModal({ ok: true, cancel: false })
      },
      onRequestClose: () => {
        closeModal({ ok: false, cancel: false })
      },
    }
    slots.default = () => h(options.contentComponent, mergedContentProps)
  }

  const vnode = h(
    ConfirmModal,
    {
      visible: true,
      title: options.title,
      content: options.content,
      type: options.type || 'info',
      showCancel: options.showCancel ?? true,
      showOk: options.showOk ?? true,
      okText: options.okText,
      cancelText: options.cancelText,
      beforeOk: options.beforeOk,
      okType: options.okType || 'primary',
      cancelType: options.cancelType || 'secondary',
      okStyle: options.okStyle,
      cancelStyle: options.cancelStyle,
      showIcon: options.showIcon ?? true,
      width: options.width,
      closable: options.closable ?? false,
      maskClosable: options.maskClosable ?? true,
      bottomSheetOnMobile: options.bottomSheetOnMobile ?? false,
      mobileFullscreen: options.mobileFullscreen ?? false,
      onOk: () => {
        options.onOk?.()
      },
      onCancel: () => {
        options.onCancel?.()
      },
      onClose: (result) => {
        handleClose(result)
      },
    },
    slots
  )

  const { vueApp } = useNuxtApp()
  vnode.appContext = vueApp._context

  render(vnode, container)

  return {
    close: (result = { ok: false, cancel: false }) => {
      closeModal(result)
    },
  }
}

/**
 * @description 信息面板弹窗调用：用于展示大块只读信息，不承载二次确认语义
 * @param {Object} options
 * @param {string} options.title - 标题
 * @param {string} options.okText - 底部按钮文案
 * @param {boolean} options.showOk - 是否展示底部按钮
 * @param {number|string} options.width - 弹窗宽度
 * @param {Object} options.contentComponent - 内容组件
 * @param {Object} options.contentProps - 内容组件 Props
 * @param {Object} options.contentSlots - 自定义插槽
 * @param {string|Array|Object} options.panelClass - 信息面板根节点自定义类名
 * @param {Function} options.onOk - 点击底部按钮回调
 * @param {Function} options.onClose - 关闭回调
 * @returns {{ close: Function }|undefined}
 */
export const infoPanel = (options = {}) => {
  if (!import.meta.client) return

  const container = document.createElement('div')
  document.body.appendChild(container)
  let isClosed = false

  const handleClose = () => {
    if (isClosed) return
    isClosed = true
    options.onClose?.()
    render(null, container)
    container.parentNode?.removeChild(container)
  }

  const slots = options.contentSlots ? { ...options.contentSlots } : {}
  if (!slots.default && options.contentComponent) {
    slots.default = () => h(options.contentComponent, options.contentProps || {})
  }

  const vnode = h(
    InfoPanelModal,
    {
      visible: true,
      title: options.title,
      okText: options.okText,
      showOk: options.showOk ?? true,
      width: options.width,
      panelClass: options.panelClass,
      onOk: () => {
        options.onOk?.()
      },
      onClose: handleClose,
    },
    slots
  )

  const { vueApp } = useNuxtApp()
  vnode.appContext = vueApp._context

  render(vnode, container)

  return {
    close: handleClose,
  }
}

const CommonModal = {
  confirm,
  infoPanel,
  info: (options) => confirm({ ...options, type: 'info', showCancel: false }),
  success: (options) => confirm({ ...options, type: 'success', showCancel: false }),
  warning: (options) => confirm({ ...options, type: 'warning', showCancel: false }),
  error: (options) => confirm({ ...options, type: 'error', showCancel: false }),
}

export default CommonModal
